import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';

/**
 * One Socket.IO connection per tab (ADR-0008), opened on first use and signed in with the session
 * cookie. It reconnects by itself; listen to `connect` to refetch what may have been missed.
 */
let socket: Socket | null = null;

function shared(): Socket {
  socket ??= io({ path: '/api/socket.io', transports: ['websocket'], withCredentials: true });
  return socket;
}

/** Calls `handler` for every `event` pushed by the server while the component is mounted. */
export function useRealtime<T = unknown>(event: string, handler: (payload: T) => void): void {
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });
  useEffect(() => {
    const s = shared();
    const listener = (payload: T) => latest.current(payload);
    s.on(event, listener);
    return () => {
      s.off(event, listener);
    };
  }, [event]);
}

/** Sends an event to the server (e.g. "typing"); fire and forget. */
export function emitRealtime(event: string, payload: unknown): void {
  shared().emit(event, payload);
}
